import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useQueryCRUD } from '../useQueryCRUD';
import servicesService from '../../services/servicesService';
import professionalsService from '../../services/professionalsService';
import personsService from '../../services/personsService';
import placesService from '../../services/placesService';
import schedulesService from '../../services/schedulesService';
import paymentsService from '../../services/paymentsService';
import pagesService from '../../services/pagesService';
import scheduleBlocksService from '../../services/scheduleBlocksService';
import api from '../../services/api';

/**
 * Hook para gerenciar Serviços com React Query
 */
export function useServices(options) {
  return useQueryCRUD('services', servicesService, options);
}

/**
 * Hook para gerenciar Profissionais com React Query
 */
export function useProfessionals(options) {
  return useQueryCRUD('professionals', professionalsService, options);
}

/**
 * Hook para gerenciar Pessoas com React Query
 */
export function usePersons(options) {
  return useQueryCRUD('persons', personsService, options);
}

/**
 * Hook para gerenciar Locais com React Query
 */
export function usePlaces(options) {
  return useQueryCRUD('places', placesService, options);
}

/**
 * Hook para gerenciar Agendas com React Query
 * Inclui dados relacionados (professionals, places, placeProfessionals)
 */
export function useSchedules(options = {}) {
  const queryClient = useQueryClient();

  // Query para listar schedules com dados relacionados
  const query = useQuery({
    queryKey: ['schedules'],
    queryFn: async () => {
      const data = await schedulesService.getAllWithRelations();
      return data;
    },
    staleTime: options.staleTime ?? 5 * 60 * 1000, // 5 minutos
    refetchOnMount: 'always', // sempre refetch ao montar (evita dado stale pós-wizard)
    ...options.queryOptions,
  });

  // Mutation para criar
  const createMutation = useMutation({
    mutationFn: (data) => schedulesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
  });

  // Mutation para atualizar
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => schedulesService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
  });

  // Mutation para deletar
  const deleteMutation = useMutation({
    mutationFn: (id) => schedulesService.delete(id),
    onSuccess: (_, id) => {
      // Remove imediatamente do cache para evitar que loadShiftsForAgendas
      // tente buscar turnos de uma agenda já deletada (race condition com staleTime)
      queryClient.setQueryData(['schedules'], (old) => {
        if (!old) return old;
        return {
          ...old,
          schedules: old.schedules?.filter((s) => s.id !== id) || [],
        };
      });
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
  });

  return {
    // Data normalizada
    schedules: query.data?.schedules || [],
    professionals: query.data?.professionals || [],
    places: query.data?.places || [],
    
    // Loading states
    loading: query.isLoading,
    isFetching: query.isFetching,
    
    // Error
    error: query.error,
    
    // Actions
    reload: () => queryClient.invalidateQueries({ queryKey: ['schedules'] }),
    
    // CRUD operations
    create: createMutation.mutate,
    createAsync: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    
    update: (id, data) => updateMutation.mutate({ id, data }),
    updateAsync: (id, data) => updateMutation.mutateAsync({ id, data }),
    isUpdating: updateMutation.isPending,
    
    deleteItem: deleteMutation.mutate,
    deleteAsync: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
}

/**
 * Hook para gerenciar Agendas (alias para compatibilidade)
 */
export function useAgendas(options) {
  return useSchedules(options);
}

/**
 * Hook para gerenciar Bloqueios/Ausências com React Query e paginação
 */
export function useScheduleBlocks(options = {}) {
  const queryClient = useQueryClient();
  const { page = 1, pageSize = 10, filters = {} } = options;

  // Query para listar bloqueios com paginação
  const query = useQuery({
    queryKey: ['scheduleBlocks', page, pageSize, filters],
    queryFn: async () => {
      const data = await scheduleBlocksService.getAll(page, pageSize, filters);
      return data;
    },
    staleTime: options.staleTime ?? 5 * 60 * 1000,
    keepPreviousData: true,
    ...options.queryOptions,
  });

  // Mutation para criar
  const createMutation = useMutation({
    mutationFn: (data) => scheduleBlocksService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduleBlocks'] });
    },
  });

  // Mutation para atualizar
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => scheduleBlocksService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduleBlocks'] });
    },
  });

  // Mutation para deletar
  const deleteMutation = useMutation({
    mutationFn: (id) => scheduleBlocksService.delete(id),
    onSuccess: (_, id) => {
      queryClient.setQueryData(['scheduleBlocks', page, pageSize, filters], (old) => {
        if (!old) return old;
        return {
          ...old,
          scheduleBlocks: old.scheduleBlocks?.filter((b) => b.id !== id) || [],
        };
      });
      queryClient.invalidateQueries({ queryKey: ['scheduleBlocks'] });
    },
  });

  return {
    // Data
    data: query.data,
    scheduleBlocks: query.data?.scheduleBlocks || [],
    blocks: query.data?.scheduleBlocks || [], // alias para compatibilidade
    professionals: query.data?.professionals || [],
    places: query.data?.places || [],
    totalCount: query.data?.totalCount || 0,
    totalPages: query.data?.totalPages || 0,
    
    // Loading states
    loading: query.isLoading,
    isFetching: query.isFetching,
    
    // Error
    error: query.error,
    
    // Actions
    reload: () => queryClient.invalidateQueries({ queryKey: ['scheduleBlocks'] }),
    
    // CRUD operations
    create: createMutation.mutate,
    createAsync: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    
    update: (id, data) => updateMutation.mutate({ id, data }),
    updateAsync: (id, data) => updateMutation.mutateAsync({ id, data }),
    isUpdating: updateMutation.isPending,
    
    deleteItem: deleteMutation.mutate,
    deleteAsync: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
}

/**
 * Hook para gerenciar Agendamentos com React Query SEM CACHE
 * Sempre busca dados frescos do servidor
 */
export function useAppointments(options = {}) {
  const queryClient = useQueryClient();
  const { startDate, endDate } = options;

  // Query para listar agendamentos - SEM CACHE
  const query = useQuery({
    queryKey: ['appointments', startDate, endDate],
    queryFn: async () => {
      const response = await api.get('/appointments', {
        params: { startDate, endDate }
      });
      return response.data || {};
    },
    staleTime: 0, // ⚠️ SEM CACHE - sempre refetch
    cacheTime: 0, // Não manter em cache
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
    ...options.queryOptions,
  });

  // Mutation para confirmar
  const confirmMutation = useMutation({
    mutationFn: (id) => api.patch(`/appointments/${id}/confirm`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
    },
  });

  // Mutation para cancelar
  const cancelMutation = useMutation({
    mutationFn: ({ id, reason }) => api.patch(`/appointments/${id}/cancel`, { cancellationReason: reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
    },
  });

  // Mutation para marcar como atendido
  const markAttendedMutation = useMutation({
    mutationFn: (id) => api.patch(`/appointments/${id}/attended`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
    },
  });

  return {
    // Data normalizada
    appointments: query.data?.appointments || [],
    professionals: query.data?.professionals || [],
    services: query.data?.services || [],
    places: query.data?.places || [],
    
    // Loading states
    loading: query.isLoading,
    isFetching: query.isFetching,
    
    // Error
    error: query.error,
    
    // Actions
    reload: () => queryClient.invalidateQueries({ queryKey: ['appointments'] }),
    
    // Operations
    confirm: confirmMutation.mutate,
    confirmAsync: confirmMutation.mutateAsync,
    isConfirming: confirmMutation.isPending,
    
    cancel: cancelMutation.mutate,
    cancelAsync: cancelMutation.mutateAsync,
    isCanceling: cancelMutation.isPending,
    
    markAttended: markAttendedMutation.mutate,
    markAttendedAsync: markAttendedMutation.mutateAsync,
    isMarkingAttended: markAttendedMutation.isPending,
  };
}

/**
 * Hook para gerenciar Métodos de Pagamento com React Query
 */
export function usePayments(options) {
  return useQueryCRUD('payments', paymentsService, options);
}

/**
 * Hook para gerenciar Páginas com React Query
 */
export function usePages(options) {
  return useQueryCRUD('pages', pagesService, options);
}

/**
 * Hook para detecção e unificação de pessoas duplicadas.
 * Busca pares de duplicatas e expõe mutation de merge.
 */
export function useDuplicatePersons() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['personDuplicates'],
    queryFn: () => personsService.getDuplicates(),
    staleTime: 5 * 60 * 1000,
  });

  const mergeMutation = useMutation({
    mutationFn: (data) => personsService.mergePersons(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['personDuplicates'] });
      queryClient.invalidateQueries({ queryKey: ['persons'] });
    },
  });

  return {
    duplicates: query.data || [],
    loading: query.isLoading,
    error: query.error,
    merge: mergeMutation.mutateAsync,
    isMerging: mergeMutation.isPending,
    reload: () => queryClient.invalidateQueries({ queryKey: ['personDuplicates'] }),
  };
}

// Exportar todos juntos
export default {
  useServices,
  useProfessionals,
  usePersons,
  usePlaces,
  useSchedules,
  useAgendas,
  useScheduleBlocks,
  useAppointments,
  usePayments,
  usePages,
  useDuplicatePersons,
};

